import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  type AccordionItemProps,
  AccordionPanel,
} from '@ironhack/design-system2/components/accordion';
import { Flex } from '@ironhack/design-system2/components/flex';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import { type ReactElement } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';

import { Button } from '@/app/components/client/button';
import { StructuredContent } from '@/app/components/client/structured-content';
import { CmsImage } from '@/app/components/server/cms-image';
import {
  type DatoBlock,
  type DatoButtonBlock,
  type DatoImage,
} from '@/lib/datocms/types';

export type ExpandCollapseCard = DatoBlock<{
  button?: [DatoButtonBlock];
  content: StructuredTextGraphQlResponse;
  filterValues?: string[];
  image?: DatoImage;
  onClick?: () => void;
  showImage?: boolean;
  showTitle?: boolean;
  title?: string;
}>;

type Props = {
  card: ExpandCollapseCard;
  headingProps?: TextProps;
} & AccordionItemProps;

export const ExpandCollapseCard = (props: Props): ReactElement => {
  const { card, headingProps, ...accordionItemProps } = props;
  const {
    button: [button] = [],
    content,
    image,
    onClick,
    showImage = false,
    showTitle = true,
    title,
  } = card;

  return (
    <AccordionItem {...accordionItemProps}>
      <AccordionButton>
        <Flex direction="column" flex={1} gap={2}>
          {showTitle && title && (
            <Text
              align="start"
              color="text.primary"
              flex={1}
              textStyle="xlBold"
              {...headingProps}
            >
              {title}
            </Text>
          )}
          {showImage && image && <CmsImage data={image.responsiveImage} />}
        </Flex>
        <AccordionIcon color="icon.dark" />
      </AccordionButton>
      <AccordionPanel as={Flex} direction="column" gap={3}>
        <StructuredContent data={content} gap={2} />
        {button && (
          <Button
            {...(onClick && { onClick })}
            alignSelf="start"
            size="s"
            width={['full', null, 'auto']}
            {...button}
          />
        )}
      </AccordionPanel>
    </AccordionItem>
  );
};
